import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import {
  ExpandLess,
  ExpandMore,
  Dashboard as DashboardIcon,
  BarChart as AnalyticsIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasMedNecAccess, hasAdminAccess } from '../../../../profile/utils/profileUtils';

interface MedNecMenuItemConfig {
  id: string;
  title: string;
  icon: React.ComponentType<{ sx?: any }>;
  route: string;
  adminOnly?: boolean;
}

const MENU_STYLES = {
  navList: { border: 'black solid 1px' },
  nestedListItem: { marginTop: '0px', paddingLeft: '48px' },
  icon: { color: 'white' },
} as const;

const MEDNEC_MENU_ITEMS: MedNecMenuItemConfig[] = [
  { id: 'dashboard', title: 'Dashboard', icon: DashboardIcon, route: '/mednec' },
  { id: 'analytics', title: 'Analytics', icon: AnalyticsIcon, route: '/mednec/analytics', adminOnly: true },
  { id: 'chat', title: 'Chat', icon: ChatIcon, route: '/mednec/chat' },
];

const MedNecMenuItem: React.FC<{ item: MedNecMenuItemConfig; onClick: () => void }> = ({ item, onClick }) => {
  const { title, icon: Icon } = item;
  return (
    <ListItemButton sx={MENU_STYLES.nestedListItem} onClick={onClick}>
      <ListItemIcon><Icon sx={MENU_STYLES.icon} /></ListItemIcon>
      <ListItemText primary={title} />
    </ListItemButton>
  );
};

export const MedNecMenu: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const selectedCenter = useAppSelector(state => state.center.selectedCenter);
  const { data: profile } = useGetMyProfileQuery();
  const company = profile?.organization || '';

  const handleItemClick = (item: MedNecMenuItemConfig): void => {
    const params = new URLSearchParams();
    if (company) params.append('company', company);
    if (selectedCenter) params.append('center', selectedCenter);
    navigate(`${item.route}?${params.toString()}`);
  };

  if (!hasMedNecAccess(profile)) return null;

  const isAdmin = hasAdminAccess(profile);
  const visibleItems = MEDNEC_MENU_ITEMS.filter(item => !item.adminOnly || isAdmin);

  return (
    <AuthenticatedTemplate>
      <List component="nav" sx={MENU_STYLES.navList}>
        <ListItemButton onClick={() => setOpen(!open)}>
          <ListItemText primary="Medical Necessity" />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          {visibleItems.map((item) => (
            <MedNecMenuItem key={item.id} item={item} onClick={() => handleItemClick(item)} />
          ))}
        </Collapse>
      </List>
    </AuthenticatedTemplate>
  );
};

export default MedNecMenu;
